import React from 'react';
import { useSelector } from 'react-redux';
import useTranslation from '@src/hooks/useTranslation';
import styled from 'styled-components';
import { Progress } from 'antd';

import { IColors } from '@src/types/theme';
import * as selectors from '@src/store/selectors';
import {
	isFundraising, isPreparing, isFundraisingFailed, isFundraisingRefund, formatCourseStartTime,
} from '@src/utils/course';
import { PROGRESS_STATUS_DONE, PROGRESS_STATUS_IN_PROGRESS, PROGRESS_STATUS_UNDONE } from '@src/const/course';
import { TCourseStatus, IProgressList } from '@src/types/course';

import { RoundBookIcon, RoundCryIcon } from '@src/components/common/icon';
import Timer from '@src/components/common/timer';
import ProgressBar from './progressBar';

const StatusWrapper = styled.div`
	display: flex;
	align-items: center;
	.status-wrapper__icon {
		width: 67px;
		height: 67px;
	}
`;

const StatusContent = styled.div`
	margin-left: 12px;
	font-size: 14px;
`;

const StatusDescription = styled.div`
	margin-bottom: 4px;
	font-size: 18px;
	color: #101010;
`;

const ProgressWrap = styled.div`
	margin-right: 12px;
`;

const RowDiv = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
`;

const StyleHr = styled.div`
	height: 12px;
	width: 1px;
	background-color: #DDD;
	margin: 0px 12px;
`;

const StyleProgress = styled(Progress)`
	.ant-progress-text {
		color: #222222 !important;
	}
`;

const getProgressList = ({ t, status, preparationEndTime, accessRight }): IProgressList[] => {
	if (isFundraising(status)) {
		return [{
			status: PROGRESS_STATUS_IN_PROGRESS,
			text: t('course:progress:fundraising'),
		}, {
			status: PROGRESS_STATUS_UNDONE,
			text: t('course:progress:finish_fundraising'),
		}, {
			status: PROGRESS_STATUS_UNDONE,
			text: (
				<RowDiv>
					<span>{`${t('course:progress:prepared_end_time')}`}</span>
					<span>{`${formatCourseStartTime(preparationEndTime)}`}</span>
					<StyleHr />
					<span>{`${accessRight}`}</span>
				</RowDiv>
			),
		}];
	} else if (isPreparing(status)) {
		return [{
			status: PROGRESS_STATUS_DONE,
			text: t('course:progress:start_fundraising'),
		}, {
			status: PROGRESS_STATUS_IN_PROGRESS,
			text: t('course:progress:fundraising_success'),
		}, {
			status: PROGRESS_STATUS_UNDONE,
			text: (
				<RowDiv>
					<span>{`${t('course:progress:prepared_end_time')}`}</span>
					<span>{`${formatCourseStartTime(preparationEndTime)}`}</span>
					<StyleHr />
					<span>{`${accessRight}`}</span>
				</RowDiv>
			),
		}];
	} else if (isFundraisingFailed(status)) {
		return [{
			status: PROGRESS_STATUS_DONE,
			text: t('course:progress:start_fundraising'),
		}, {
			status: PROGRESS_STATUS_IN_PROGRESS,
			text: t('course:progress:finish_fundraising'),
		}, {
			status: PROGRESS_STATUS_UNDONE,
			text: t('course:progress:go_to_class'),
		}];
	} else if (isFundraisingRefund(status)) {
		return [{
			status: PROGRESS_STATUS_DONE,
			text: t('course:progress:start_fundraising'),
		}, {
			status: PROGRESS_STATUS_DONE,
			text: t('course:progress:finish_fundraising'),
		}, {
			status: PROGRESS_STATUS_DONE,
			text: t('course:progress:refund'),
		}];
	}

	return [];
};

const renderChildren = ({ t, colors, status, fundraisingEndTime, percentage, showStudents }): JSX.Element => {
	if (isFundraising(status)) {
		return (
			<>
				<ProgressWrap>
					{
						showStudents ? (
							<StyleProgress
								strokeColor={{
									'0%': '#A6C0E5',
									'100%': '#587DB1',
								}}
								type="circle"
								percent={percentage}
								format={() => `${percentage} %`}
								width={60}
							/>
						) : (
							<RoundBookIcon iconColor={colors.primary} className="status-wrapper__icon" />
						)
					}
				</ProgressWrap>
				<Timer timerTitle={t('course:fundraising_countdown')} endTime={fundraisingEndTime} />
			</>
		);
	} else if (isPreparing(status)) {
		return (
			<StatusWrapper>
				{
					showStudents ? (
						<StyleProgress
							strokeColor={{
								'0%': '#A6C0E5',
								'100%': '#587DB1',
							}}
							type="circle"
							percent={percentage}
							format={() => `${percentage} %`}
							width={60}
						/>
					) : (
						<RoundBookIcon iconColor={colors.primary} className="status-wrapper__icon" />
					)
				}
				<StatusContent>
					<StatusDescription>{t('course:prepare_class')}</StatusDescription>
					{t('course:prepare_class_description')}
				</StatusContent>
			</StatusWrapper>
		);
	} else if (isFundraisingFailed(status)) {
		return (
			<StatusWrapper>
				<RoundBookIcon iconColor={colors.primary} className="status-wrapper__icon" />
				<StatusContent>
					<StatusDescription>{t('course:prepare_class')}</StatusDescription>
					{t('course:prepare_class_description')}
				</StatusContent>
			</StatusWrapper>
		);
	} else if (isFundraisingRefund(status)) {
		return (
			<StatusWrapper>
				<RoundCryIcon iconColor={colors.primary} className="status-wrapper__icon" />
				<StatusContent>
					<StatusDescription>{t('course:fundraising_refund')}</StatusDescription>
					{t('course:fundraising_refund_description')}
				</StatusContent>
			</StatusWrapper>
		);
	}

	return null;
};

interface IProgressContent {
	status: TCourseStatus;
	fundraisingEndTime?: number;
	preparationEndTime?: number;
	percentage?: number;
	showStudents?: boolean;
	accessRight?: string;
}

const ProgressContent = ({
	status,
	fundraisingEndTime,
	preparationEndTime,
	percentage,
	showStudents,
	accessRight,
}: IProgressContent): JSX.Element => {
	const { t } = useTranslation();
	const { colors } = useSelector(selectors.getColorTheme) as { colors: IColors };
	const progressList = getProgressList({ t, status, preparationEndTime, accessRight });

	return (
		<ProgressBar progressList={progressList}>
			{renderChildren({ t, colors, status, fundraisingEndTime, percentage, showStudents })}
		</ProgressBar>
	);
};

export default ProgressContent;
